import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { css } from '@emotion/react';
import { useDispatch, useSelector } from 'react-redux';
import { ResidentFilters, NotificationResidentListHeader } from '../../../components/notificationResidentListHeader';
import api from '../../../api';
import { IGetNotificationRes } from '../../../types/api/notification/getNotification';
import { NotificationListItem } from './notificationListItem';
import { selfDataSelector, showMedicalContentSelector } from '../../../redux/config/selfData';
import { filterConditionSelector, residentListSettingsSlice } from '../../../redux/config/residentListSettings';
import { useLoading } from '../../../contexts/LoadingContext';
import { IGetResidentActiveRes } from '@/types/api/resident/getResidentActive';
import { useFetchResidents } from '@/hooks/api/resident/useFetchResidents';

interface ResidentNotification extends IGetNotificationRes {
  residentName: string;
  residentLastName: string;
}

const NotificationListContainer = css`
  border-radius: 8px;
  overflow: hidden;
`;

const Container = css`
  padding: 0 16px 16px;
`;

const EmptyResidentContainer = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  font-size: 16px;
`;

const initialFilters: ResidentFilters = {
  categoryCondition: 'all',
  unitId: null,
  residentId: null,
  notificationTypes: [],
};

export const Notification: React.VFC = () => {
  const createFilterType = (condition: 'all' | 'deviceAssigned' | 'deviceAttached' | 'unit') => {
    if (['deviceAssigned', 'deviceAttached'].includes(condition)) {
      return condition as 'deviceAssigned' | 'deviceAttached';
    } else return null;
  };

  const { nursingHomeId } = useParams<{ nursingHomeId: string }>();
  const dispatch = useDispatch();
  const { selfUser, selfUnits } = useSelector(selfDataSelector);
  const showMedicalContent = useSelector(showMedicalContentSelector);
  const { conditions, unitId } = useSelector(filterConditionSelector);
  const { residents } = useFetchResidents(true);
  const { showLoading, hideLoading } = useLoading();
  const { t } = useTranslation();
  const [residentNotifications, setResidentNotifications] = React.useState<ResidentNotification[]>([]);
  const [filteredResidentIds, setFilteredResidentIds] = React.useState<number[]>([]);
  const currentUnitId = React.useMemo(
    () => (selfUser.hasUnitPerm ? selfUser.nursingHomeUnitId : unitId),
    [selfUser.hasUnitPerm, selfUser.nursingHomeUnitId, unitId],
  );
  const [filterState, setFilterState] = React.useState<ResidentFilters>({
    ...initialFilters,
    categoryCondition: conditions,
    unitId: currentUnitId,
  });

  const fetchNotifications = React.useCallback(
    async (selectedUnitId: number | null = currentUnitId, condition?: 'deviceAssigned' | 'deviceAttached' | null) => {
      showLoading();
      try {
        const params = {
          nursingHomeId,
          limit: 50,
          sort: 'desc',
          ...(selectedUnitId ? { nursingHomeUnitId: selectedUnitId } : {}),
        };
        const [notifications, gasNotifications] = await Promise.all([
          api.get('/nursing-homes/:nursingHomeId/notifications', {
            params: params,
          }),
          showMedicalContent
            ? Promise.resolve([] as IGetNotificationRes[])
            : api.get('/nursing-homes/:nursingHomeId/notifications', {
                params: {
                  ...params,
                  notificationClass: 1,
                },
              }),
        ]);
        const margedNotifications: IGetNotificationRes[] = [...notifications, ...gasNotifications];

        const activeResidents: IGetResidentActiveRes[] = await api.get(
          '/nursing-homes/:nursingHomeId/residents/active',
          {
            params: { nursingHomeId },
          },
        );

        const filteredResidents = (() => {
          if (condition === 'deviceAssigned') {
            return activeResidents.filter(
              (resident) => resident.serialNumber !== null || resident.gasSerialNumber !== null,
            );
          } else if (condition === 'deviceAttached') {
            return activeResidents.filter(
              (resident) => resident.urinePercentage !== null || resident.gasPercentage !== null,
            );
          } else {
            return activeResidents;
          }
        })();
        setFilteredResidentIds(filteredResidents.map((resident) => resident.id));

        const residentNameMap = new Map(
          filteredResidents.map((resident) => [resident.id, { name: resident.name, lastName: resident.lastName }]),
        );
        const filteredNotifications = margedNotifications
          .filter((notification) => residentNameMap.has(notification.residentId))
          .sort((a, b) => {
            if (a.timestamp < b.timestamp) return 1;
            if (a.timestamp > b.timestamp) return -1;
            return 0;
          })
          .map((notification) => {
            const resident = residentNameMap.get(notification.residentId);
            return {
              ...notification,
              residentName: resident?.name ?? '',
              residentLastName: resident?.lastName ?? '',
            };
          });
        setResidentNotifications(filteredNotifications);
      } finally {
        hideLoading();
      }
    },
    [currentUnitId, hideLoading, nursingHomeId, showLoading, showMedicalContent],
  );

  const handleUpdateNotifications = React.useCallback(() => {
    const selectedCondition = createFilterType(conditions);
    fetchNotifications(currentUnitId, selectedCondition);
  }, [conditions, currentUnitId, fetchNotifications]);

  const handleChangeFilters = React.useCallback(
    (filters: ResidentFilters) => {
      const updatedFilters = selfUser.hasUnitPerm ? { ...filters, unitId: selfUser.nursingHomeUnitId } : filters;
      const isUnitChanged = filterState.unitId !== updatedFilters.unitId;
      const isConditionChanged = filterState.categoryCondition !== updatedFilters.categoryCondition;
      setFilterState(updatedFilters);

      dispatch(
        residentListSettingsSlice.actions.setFilterCondition({
          conditions: updatedFilters.categoryCondition,
          unitId: updatedFilters.unitId,
        }),
      );
      localStorage.setItem('condition', updatedFilters.categoryCondition);
      localStorage.setItem('unit', String(updatedFilters.unitId));

      if (isUnitChanged || isConditionChanged) {
        fetchNotifications(updatedFilters.unitId, createFilterType(updatedFilters.categoryCondition));
      }
    },
    [
      dispatch,
      filterState.categoryCondition,
      filterState.unitId,
      fetchNotifications,
      selfUser.hasUnitPerm,
      selfUser.nursingHomeUnitId,
    ],
  );

  const filteredResidentNotifications = React.useMemo(() => {
    const shouldFilterByNotificationType = filterState.notificationTypes.length > 0;
    return residentNotifications.filter((notification) => {
      const shouldShowByResident = filterState.residentId ? notification.residentId === filterState.residentId : true;
      const shouldShowByNotificationType = shouldFilterByNotificationType
        ? filterState.notificationTypes.includes(notification.notificationType)
        : true;
      return shouldShowByResident && shouldShowByNotificationType;
    });
  }, [filterState.notificationTypes, filterState.residentId, residentNotifications]);

  // ヘッダの絞り込み機能で使用する入居者のリスト
  const residentsForHeaderFilter = React.useMemo(() => {
    let filteredResidents = residents ?? [];
    if (filterState.unitId) {
      filteredResidents = filteredResidents.filter((resident) => resident.nursingHomeUnitId === filterState.unitId);
    }
    if (filterState.categoryCondition === 'deviceAssigned' || filterState.categoryCondition === 'deviceAttached') {
      const residentIdSet = new Set(filteredResidentIds);
      filteredResidents = filteredResidents.filter((resident) => residentIdSet.has(resident.id));
    }
    return filteredResidents;
  }, [filteredResidentIds, filterState.categoryCondition, filterState.unitId, residents]);

  React.useEffect(() => {
    if (!selfUser.serviceType) return; // 初期化されていなければfetchしない
    const selectedCondition = createFilterType(conditions);
    fetchNotifications(currentUnitId, selectedCondition);
  }, [selfUser.id]);

  React.useEffect(() => {
    setFilterState((prev) => {
      const isUnitChanged = prev.unitId !== currentUnitId;
      return {
        ...prev,
        categoryCondition: conditions,
        unitId: currentUnitId,
        residentId: isUnitChanged ? null : prev.residentId,
      };
    });
  }, [conditions, currentUnitId]);

  return (
    <div css={Container}>
      <NotificationResidentListHeader
        title={t('notification', '通知')}
        units={selfUnits}
        residents={residentsForHeaderFilter}
        filters={filterState}
        onUpdate={handleUpdateNotifications}
        onChangeFilters={handleChangeFilters}
      />
      {!filteredResidentNotifications.length ? (
        <div css={EmptyResidentContainer}>{t('noNotification', '通知はありません')}</div>
      ) : (
        <div css={NotificationListContainer}>
          {filteredResidentNotifications.map((notification) => (
            <NotificationListItem key={notification.id} notification={notification} />
          ))}
        </div>
      )}
    </div>
  );
};
